import { useState } from "react";
import { useQueryClient } from "react-query";
import {
  Dialog,
  Column,
  Row,
  Button,
  Checkbox,
  FormField,
  Text,
  Tooltip,
  useToast,
  CopyIcon,
  DownloadIcon,
} from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import {
  type ContentPersonalizationTemplateLanguage,
  type ExportEmailToPngPersonalizationInput,
  useContentEmailPersonalizationPreviewQuery,
} from "src/graphql";
import {
  buildPersonalizationPreviewOptions,
  valuesForOption,
  type PersonalizationCatalogSample,
} from "src/pages/lifecycle-studio/messages/personalization-preview-utils";
import {
  downloadedTitle,
  formatApps,
  formatList,
  isLayeredFormat,
  useExportEmailFrames,
  type EmailExportFormat,
  type ExportResult,
  type FrameScheme,
  type FrameSelection,
  type FrameWidth,
} from "src/pages/lifecycle-studio/emails/png-export/export-email";

const WIDTH_OPTIONS: { id: FrameWidth; label: string }[] = [
  { id: "desktop", label: "Web" },
  { id: "mobile", label: "Mobile" },
];

const SCHEME_OPTIONS: { id: FrameScheme; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
];

const ALL_WIDTHS = new Set<FrameWidth>(WIDTH_OPTIONS.map((o) => o.id));
const ALL_SCHEMES = new Set<FrameScheme>(SCHEME_OPTIONS.map((o) => o.id));

const LIGHT_ONLY = new Set<FrameScheme>(["light"]);

const DEFAULT_FORMATS: readonly EmailExportFormat[] = ["png"];

const TOAST_ID = "export-email-to-png";

export type ExportEmailPngPersonalization = {
  language: ContentPersonalizationTemplateLanguage;
  values: Record<string, string>;
  catalogs: PersonalizationCatalogSample[];
};

type ExportEmailPngModalProps = {
  /** Exported together: PNG renders in its own run, and the layered formats share one. */
  formats?: readonly EmailExportFormat[];
  isOpen: boolean;
  onClose: () => void;
  contentEmailId: string;
  contentEmailVersionId: string;
  filename?: string;
  /**
   * Sample data already rendered in the studio's personalization preview
   * (including the user's edited values). When absent and sample data is
   * enabled, the modal fetches fresh sample values itself.
   */
  personalization?: ExportEmailPngPersonalization;
  /**
   * Dynamic-content variant selections (slot id → branch id) from the preview
   * canvas. The backend materializes every dynamic slot regardless — this only
   * overrides which branch each slot shows (unselected slots resolve to their
   * priority-first branch).
   */
  slotSelections?: Record<string, string>;
  /** Hides the sample-data option (components, reference template). */
  isPersonalizationDisabled?: boolean;
};

function buildFrames(
  widths: Set<FrameWidth>,
  schemes: Set<FrameScheme>,
): FrameSelection[] {
  const frames: FrameSelection[] = [];
  for (const w of WIDTH_OPTIONS) {
    if (!widths.has(w.id)) continue;
    for (const s of SCHEME_OPTIONS) {
      if (!schemes.has(s.id)) continue;
      frames.push({ width: w.id, scheme: s.id });
    }
  }
  return frames;
}

function toggleInSet<T>(set: Set<T>, id: T, checked: boolean): Set<T> {
  const next = new Set(set);
  if (checked) next.add(id);
  else next.delete(id);
  return next;
}

type ExportMode = "clipboard" | "download";

export const ExportEmailPngModal = (props: ExportEmailPngModalProps) => {
  const formats = props.formats ?? DEFAULT_FORMATS;
  // Keep polling alive when changing the dialog's formats or email version.
  const pngExport = useExportEmailFrames("png");
  const layeredExport = useExportEmailFrames(formats.filter(isLayeredFormat));
  return (
    <ExportEmailPngForm
      key={`${props.contentEmailVersionId}-${formats.join("-")}`}
      {...props}
      formats={formats}
      pngExport={pngExport}
      layeredExport={layeredExport}
    />
  );
};

const ExportEmailPngForm = ({
  pngExport,
  layeredExport,
  formats,
  isOpen,
  onClose,
  contentEmailId,
  contentEmailVersionId,
  filename,
  personalization,
  slotSelections,
  isPersonalizationDisabled = false,
}: Omit<ExportEmailPngModalProps, "formats"> & {
  formats: readonly EmailExportFormat[];
  pngExport: ReturnType<typeof useExportEmailFrames>;
  layeredExport: ReturnType<typeof useExportEmailFrames>;
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const hasPng = formats.includes("png");
  const layered = formats.filter(isLayeredFormat);
  const isLoading = pngExport.isLoading || layeredExport.isLoading;
  const [widths, setWidths] = useState<Set<FrameWidth>>(ALL_WIDTHS);
  const [schemes, setSchemes] = useState<Set<FrameScheme>>(ALL_SCHEMES);
  const [activeMode, setActiveMode] = useState<ExportMode | null>(null);
  const [useSampleData, setUseSampleData] = useState(true);
  const [expandContentBlocks, setExpandContentBlocks] = useState(true);

  const isSampleDataRequested = useSampleData && !isPersonalizationDisabled;

  const previewVariables = {
    input: {
      email: { contentEmailId, versionId: contentEmailVersionId },
      sampleCount: 1,
    },
  };

  // When the studio hasn't rendered a personalization preview yet, fetch
  // fresh sample values so the export still ships with variables filled in.
  // The export buttons stay enabled while this runs — an export click awaits
  // the same query (deduped by key) in `resolvePersonalizationInput`.
  const shouldFetchSampleData =
    isOpen &&
    isSampleDataRequested &&
    !personalization &&
    Boolean(contentEmailId) &&
    Boolean(contentEmailVersionId);
  const { isLoading: isLoadingFetchedPreview, error: fetchedPreviewError } =
    useContentEmailPersonalizationPreviewQuery(previewVariables, {
      enabled: shouldFetchSampleData,
      retry: false,
    });

  const resolvePersonalizationInput =
    async (): Promise<ExportEmailToPngPersonalizationInput | null> => {
      if (!isSampleDataRequested) {
        return null;
      }
      if (personalization) {
        return {
          language: personalization.language,
          values: personalization.values,
          catalogs: personalization.catalogs,
        };
      }
      if (fetchedPreviewError) {
        // The prefetch already failed while the modal was open — fail open to
        // the raw template instead of retrying the LLM pass on click.
        return null;
      }
      try {
        const data = await queryClient.fetchQuery({
          queryKey:
            useContentEmailPersonalizationPreviewQuery.getKey(previewVariables),
          queryFn:
            useContentEmailPersonalizationPreviewQuery.fetcher(
              previewVariables,
            ),
          staleTime: Infinity,
          retry: false,
        });
        const fetched = data.getContentEmailPersonalizationPreview;
        return {
          language: fetched.language,
          values: valuesForOption(
            buildPersonalizationPreviewOptions(fetched)[0],
          ),
          catalogs: fetched.catalogs,
        };
      } catch (error) {
        // Fail-open: sample values couldn't be fetched — export the raw
        // template rather than blocking the export.
        captureException(error);
        toast({
          id: `${TOAST_ID}-personalization`,
          title: "Exporting without sample data",
          message:
            "Sample data couldn't be generated — the export will use the raw template.",
          variant: "warning",
        });
        return null;
      }
    };

  const isWaitingForSampleData =
    shouldFetchSampleData && isLoadingFetchedPreview && !fetchedPreviewError;

  const pngFrames = hasPng ? buildFrames(widths, schemes) : [];
  // Layered exports have no dark mode.
  const layeredFrames =
    layered.length > 0 ? buildFrames(widths, LIGHT_ONLY) : [];
  const selectionError =
    widths.size === 0
      ? "Select at least one device."
      : hasPng && schemes.size === 0
        ? "Select at least one color scheme."
        : null;
  const canExport = !selectionError && !isLoading && activeMode === null;
  const isPngOnly = hasPng && layered.length === 0;
  const canClipboard = canExport && isPngOnly && pngFrames.length === 1;
  const clipboardTooltip =
    canExport && !canClipboard
      ? "Clipboard copy supports one frame at a time. Pick a single frame or use Download."
      : "";

  const handleExport = async (mode: ExportMode) => {
    setActiveMode(mode);
    try {
      const opts = {
        contentEmailId,
        contentEmailVersionId,
        filename,
        mode,
        personalization: await resolvePersonalizationInput(),
        slotSelections,
        expandContentBlocks,
      };
      const runs: {
        formats: readonly EmailExportFormat[];
        result: Promise<ExportResult>;
      }[] = [];
      if (hasPng)
        runs.push({
          formats: ["png"],
          result: pngExport.exportEmailFrames({ ...opts, frames: pngFrames }),
        });
      if (layered.length > 0)
        runs.push({
          formats: layered,
          result: layeredExport.exportEmailFrames({
            ...opts,
            frames: layeredFrames,
          }),
        });
      const settled = await Promise.allSettled(runs.map((run) => run.result));
      const results: ExportResult[] = [];
      settled.forEach((outcome, index) => {
        if (outcome.status === "fulfilled") {
          results.push(outcome.value);
          return;
        }
        const failedFormats = runs[index]?.formats ?? formats;
        captureException(outcome.reason);
        toast({
          id: `${TOAST_ID}-${failedFormats.join("-")}`,
          title: `Couldn't export ${formatList(failedFormats)}`,
          message:
            outcome.reason instanceof Error
              ? outcome.reason.message
              : undefined,
          variant: "error",
        });
      });

      const personalizationWarning = results.find(
        (result) => result.personalizationWarning,
      )?.personalizationWarning;
      if (personalizationWarning) {
        toast({
          id: `${TOAST_ID}-personalization`,
          // Covers both degradations the export can report: unrendered
          // provider content blocks and partially applied sample data.
          title: "Exported with warnings",
          message: personalizationWarning,
          variant: "warning",
        });
      }

      // A failed format keeps the dialog open so it can be retried.
      if (results.length < runs.length) return;
      const [first] = results;
      if (mode === "clipboard" && first) {
        toast(
          first.kind === "clipboard"
            ? {
                id: TOAST_ID,
                title: "Copied to clipboard",
                message:
                  "Paste into Figma or any image app to place the frame.",
                variant: "success",
              }
            : {
                id: TOAST_ID,
                title: "Downloaded PNG",
                message: first.reason
                  ? `Clipboard write unavailable: ${first.reason}. The PNG was downloaded instead.`
                  : undefined,
                variant: "warning",
              },
        );
      } else {
        const count = results.reduce(
          (total, result) =>
            total + (result.kind === "download" ? result.count : 0),
          0,
        );
        toast({
          id: TOAST_ID,
          title: downloadedTitle(formats, count),
          variant: "success",
        });
      }
      onClose();
    } finally {
      setActiveMode(null);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={isPngOnly ? "Export PNG" : `Download ${formatList(formats)}`}
      variant="info"
      width="md"
      actions={
        <Row gap={2} justify="flex-end">
          <Button variant="secondary" onClick={onClose}>
            {activeMode ? "Close" : "Cancel"}
          </Button>
          {isPngOnly && (
            <Tooltip message={clipboardTooltip} isDisabled={!clipboardTooltip}>
              <Button
                icon={CopyIcon}
                onClick={() => handleExport("clipboard")}
                isDisabled={!canClipboard}
                isLoading={activeMode === "clipboard"}
              >
                Copy to clipboard
              </Button>
            </Tooltip>
          )}
          <Button
            variant="primary"
            icon={DownloadIcon}
            onClick={() => handleExport("download")}
            isDisabled={!canExport}
            isLoading={activeMode === "download"}
          >
            Download
          </Button>
        </Row>
      }
    >
      <Column gap={5}>
        {layered.length > 0 && (
          <Text size="sm" color="text.secondary">
            Text exports as editable layers when its font can be identified.
            Images and complex effects remain pixel layers. Install the email's
            fonts in {formatApps(layered)} to edit its text.
          </Text>
        )}
        {activeMode && (
          <Text size="sm" color="text.secondary">
            Preparing {formatList(formats)} download… You can close this dialog
            while it finishes.
          </Text>
        )}
        <FormField label="Device">
          <Row gap={4}>
            {WIDTH_OPTIONS.map((opt) => (
              <Checkbox
                key={opt.id}
                label={!hasPng && opt.id === "desktop" ? "Desktop" : opt.label}
                isChecked={widths.has(opt.id)}
                onChange={(e) =>
                  setWidths((prev) =>
                    toggleInSet(prev, opt.id, e.target.checked),
                  )
                }
              />
            ))}
          </Row>
        </FormField>
        {hasPng && (
          <FormField
            label="Color scheme"
            description={
              layered.length > 0
                ? `${formatList(layered)} export in light mode only.`
                : undefined
            }
          >
            <Row gap={4}>
              {SCHEME_OPTIONS.map((opt) => (
                <Checkbox
                  key={opt.id}
                  label={opt.label}
                  isChecked={schemes.has(opt.id)}
                  onChange={(e) =>
                    setSchemes((prev) =>
                      toggleInSet(prev, opt.id, e.target.checked),
                    )
                  }
                />
              ))}
            </Row>
          </FormField>
        )}
        <FormField label="Provider content blocks">
          <Column gap={1}>
            <Checkbox
              label="Expand content blocks"
              isChecked={expandContentBlocks}
              onChange={(e) => setExpandContentBlocks(e.target.checked)}
            />
            <Text size="sm" color="text.secondary">
              Renders each referenced content block's content, the way the
              preview does. Uncheck to export the raw references instead.
            </Text>
          </Column>
        </FormField>
        {!isPersonalizationDisabled && (
          <FormField label="Template variables">
            <Column gap={1}>
              <Checkbox
                label="Fill with sample data"
                isChecked={useSampleData}
                onChange={(e) => setUseSampleData(e.target.checked)}
              />
              <Text size="sm" color="text.secondary">
                Renders personalization variables with the sample values from
                the preview, so the export looks like a real recipient's email.
                Uncheck to export the raw template.
              </Text>
              {isWaitingForSampleData && (
                <Text size="sm" color="text.secondary">
                  Generating sample data… Exports will wait for it to finish.
                </Text>
              )}
              {Boolean(fetchedPreviewError) && isSampleDataRequested && (
                <Text size="sm" color="warning.base">
                  Sample data couldn't be generated — the export will use the
                  raw template.
                </Text>
              )}
            </Column>
          </FormField>
        )}
        {selectionError && (
          <Text size="sm" color="danger.text">
            {selectionError}
          </Text>
        )}
        {hasPng && schemes.has("dark") && (
          <Text size="sm" color="text.secondary">
            Dark mode is simulated for Android Gmail. The result may not match
            every client exactly.
          </Text>
        )}
        {pngFrames.length > 1 && (
          <Text size="sm" color="text.secondary">
            {pngFrames.length} frames will download as separate PNGs.
          </Text>
        )}
        {layeredFrames.length * layered.length > 1 && (
          <Text size="sm" color="text.secondary">
            {layeredFrames.length > 1 ? "Desktop and mobile " : ""}
            {formatList(layered)} files will download together in a ZIP file.
          </Text>
        )}
      </Column>
    </Dialog>
  );
};
